import { Link } from 'react-router-dom'
import { styled } from 'styled-system/jsx'

const Hero = styled('section', {
  base: {
    position: 'relative',
    overflow: 'hidden',
    paddingY: { base: '56px', md: '96px' },
    paddingX: { base: '16px', md: '32px' },
    background:
      'linear-gradient(180deg, var(--colors-book-primarySoft) 0%, var(--colors-book-surface) 90%)',
  },
})

const HeroInner = styled('div', {
  base: {
    maxWidth: '1180px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: { base: '1fr', md: '1.05fr 1fr' },
    gap: { base: '40px', md: '56px' },
    alignItems: 'center',
  },
})

const HeroCopy = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    maxWidth: '560px',
  },
})

const HeroEyebrow = styled('div', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    paddingX: '12px',
    paddingY: '6px',
    borderRadius: 'pill',
    backgroundColor: 'rgba(31, 111, 235, 0.1)',
    color: 'book.primary',
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    width: 'fit-content',
  },
})

const HeroHeadline = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: { base: '40px', md: '60px' },
    fontWeight: 700,
    lineHeight: 1.02,
    letterSpacing: '-0.025em',
    color: 'app.fg',
    margin: 0,
  },
})

const HeroAccent = styled('span', {
  base: {
    background:
      'linear-gradient(135deg, var(--colors-book-primary) 0%, #4F8DF8 100%)',
    backgroundClip: 'text',
    color: 'transparent',
  },
})

const HeroSubhead = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: { base: '17px', md: '19px' },
    lineHeight: 1.55,
    color: 'app.fgMuted',
    margin: 0,
  },
})

const HeroActions = styled('div', {
  base: {
    marginTop: '8px',
    display: 'flex',
    flexDirection: { base: 'column', sm: 'row' },
    gap: '12px',
  },
})

const PrimaryCta = styled('a', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: 'app.accent',
    paddingX: '24px',
    paddingY: '14px',
    borderRadius: 'pill',
    textDecoration: 'none',
    textAlign: 'center',
    transition: 'background-color 160ms, transform 160ms',
    _hover: {
      backgroundColor: 'app.accentHover',
      textDecoration: 'none',
      transform: 'translateY(-1px)',
    },
  },
})

const SecondaryCta = styled(Link, {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: 600,
    color: 'app.fg',
    backgroundColor: 'app.surface',
    paddingX: '24px',
    paddingY: '14px',
    borderRadius: 'pill',
    textDecoration: 'none',
    textAlign: 'center',
    border: '1px solid',
    borderColor: 'app.border',
    transition: 'border-color 160ms, background-color 160ms',
    _hover: {
      borderColor: 'book.borderStrong',
      backgroundColor: 'book.surfaceMuted',
      textDecoration: 'none',
    },
  },
})

const HeroFinePrint = styled('div', {
  base: {
    marginTop: '8px',
    fontFamily: 'body',
    fontSize: '13px',
    color: 'app.fgMuted',
  },
})

const HeroVisual = styled('div', {
  base: {
    width: '100%',
  },
})

const ProductPreviewPlaceholder = styled('div', {
  base: {
    width: '100%',
    aspectRatio: '16 / 11',
    backgroundColor: 'app.surface',
    border: '1px dashed',
    borderColor: 'book.borderStrong',
    borderRadius: 'lg',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '24px',
    color: 'app.fgMuted',
    fontFamily: 'body',
    fontSize: '14px',
    textAlign: 'center',
    boxShadow:
      '0 1px 2px rgba(15, 23, 42, 0.04), 0 12px 32px rgba(15, 23, 42, 0.08)',
  },
})

const PlaceholderLabel = styled('div', {
  base: {
    fontFamily: 'display',
    fontSize: '15px',
    fontWeight: 600,
    color: 'app.fg',
  },
})

const PlaceholderHint = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'app.fgMuted',
    maxWidth: '280px',
  },
})

const SectionShell = styled('section', {
  base: {
    paddingY: { base: '64px', md: '96px' },
    paddingX: { base: '16px', md: '32px' },
  },
  variants: {
    background: {
      surface: { backgroundColor: 'app.surface' },
      muted: { backgroundColor: 'app.bg' },
    },
  },
  defaultVariants: { background: 'surface' },
})

const SectionInner = styled('div', {
  base: {
    maxWidth: '1180px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
  },
})

const SectionHead = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '720px',
  },
})

const SectionEyebrow = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: 600,
    color: 'book.primary',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
})

const SectionTitle = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: { base: '30px', md: '40px' },
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: 'app.fg',
    margin: 0,
  },
})

const SectionSubhead = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: { base: '16px', md: '18px' },
    lineHeight: 1.55,
    color: 'app.fgMuted',
    margin: 0,
  },
})

const BenefitsGrid = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: { base: '1fr', md: 'repeat(3, 1fr)' },
    gap: { base: '20px', md: '24px' },
  },
})

const BenefitCard = styled('article', {
  base: {
    backgroundColor: 'app.surface',
    border: '1px solid',
    borderColor: 'app.border',
    borderRadius: 'lg',
    padding: { base: '24px', md: '28px' },
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
})

const BenefitNumber = styled('div', {
  base: {
    fontFamily: 'display',
    fontSize: '14px',
    fontWeight: 600,
    color: 'book.primary',
    letterSpacing: '0.06em',
  },
})

const BenefitTitle = styled('h3', {
  base: {
    fontFamily: 'display',
    fontSize: '22px',
    fontWeight: 600,
    color: 'app.fg',
    margin: 0,
    lineHeight: 1.2,
  },
})

const BenefitBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    lineHeight: 1.55,
    color: 'app.fgMuted',
    margin: 0,
  },
})

const FeaturesGrid = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: { base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
    gap: { base: '20px', md: '24px' },
  },
})

const FeatureCard = styled('article', {
  base: {
    backgroundColor: 'app.surface',
    border: '1px solid',
    borderColor: 'app.border',
    borderRadius: 'lg',
    padding: { base: '24px', md: '28px' },
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    transition: 'transform 200ms, box-shadow 200ms, border-color 200ms',
    _hover: {
      transform: 'translateY(-2px)',
      boxShadow:
        '0 1px 2px rgba(15, 23, 42, 0.04), 0 12px 24px rgba(15, 23, 42, 0.08)',
      borderColor: 'book.borderStrong',
    },
  },
})

const FeatureTitle = styled('h3', {
  base: {
    fontFamily: 'display',
    fontSize: '18px',
    fontWeight: 600,
    color: 'app.fg',
    margin: 0,
    lineHeight: 1.25,
  },
})

const FeatureBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14.5px',
    lineHeight: 1.55,
    color: 'app.fgMuted',
    margin: 0,
  },
})

const SectionFooterLink = styled(Link, {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: 600,
    color: 'app.accent',
    textDecoration: 'none',
    width: 'fit-content',
    _hover: { textDecoration: 'underline' },
  },
})

const UseCaseStrip = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: { base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
    gap: '12px',
  },
})

const UseCasePill = styled(Link, {
  base: {
    backgroundColor: 'app.surface',
    border: '1px solid',
    borderColor: 'app.border',
    borderRadius: 'md',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '10px',
    textDecoration: 'none',
    color: 'app.fg',
    transition: 'border-color 160ms, transform 160ms',
    _hover: {
      borderColor: 'app.accent',
      transform: 'translateY(-1px)',
      textDecoration: 'none',
    },
  },
})

const UseCasePillTitle = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: 600,
    color: 'app.fg',
  },
})

export {
  Hero,
  HeroInner,
  HeroCopy,
  HeroEyebrow,
  HeroHeadline,
  HeroAccent,
  HeroSubhead,
  HeroActions,
  PrimaryCta,
  SecondaryCta,
  HeroFinePrint,
  HeroVisual,
  ProductPreviewPlaceholder,
  PlaceholderLabel,
  PlaceholderHint,
  SectionShell,
  SectionInner,
  SectionHead,
  SectionEyebrow,
  SectionTitle,
  SectionSubhead,
  BenefitsGrid,
  BenefitCard,
  BenefitNumber,
  BenefitTitle,
  BenefitBody,
  FeaturesGrid,
  FeatureCard,
  FeatureTitle,
  FeatureBody,
  SectionFooterLink,
  UseCaseStrip,
  UseCasePill,
  UseCasePillTitle,
}
